import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Clock, Users, Loader } from 'lucide-react';
import { theme } from '../../theme';
import { apiUtils } from '../../utils/apiUtils';

interface CreateMatchFromBookingModalProps {
  bookingId: string;
  onClose: () => void;
  onSuccess: (matchId: string) => void;
}

interface BookingPrefillData {
  title: string;
  date: string;
  time: string;
  endTime: string;
  location: string;
  googleMapsUrl: string;
  maxPlayers: number;
  venue: {
    id: string;
    name: string;
    type: string;
    address: string;
    complexName: string | null;
  };
  bookingId: string;
  totalAmount?: number;
}

export const CreateMatchFromBookingModal: React.FC<CreateMatchFromBookingModalProps> = ({
  bookingId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefillData, setPrefillData] = useState<BookingPrefillData | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    maxPlayers: 10,
    price: 0,
    team1Name: 'Equipo 1',
    team2Name: 'Equipo 2',
    team1Color: '#4CAF50',
    team2Color: '#2196F3',
  });

  useEffect(() => {
    fetchPrefillData();
  }, [bookingId]);

  const fetchPrefillData = async () => {
    try {
      setLoading(true);
      const response = await apiUtils.get(`/api/bookings/${bookingId}/match-prefill`);
      setPrefillData(response.data);

      // Pre-llenar form - el precio es el total de la reserva, se divide auto entre jugadores
      setFormData(prev => ({
        ...prev,
        title: response.data.title,
        maxPlayers: response.data.maxPlayers,
        price: response.data.totalAmount || 0,
      }));

      setLoading(false);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error fetching prefill data:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiUtils.post(`/api/bookings/${bookingId}/create-match`, formData);

      // Success
      onSuccess(response.data.match.id);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error creating match:', err);
      setError(err.response?.data?.error || 'Error al crear partido');
      setCreating(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !creating) {
      onClose();
    }
  };

  // Styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
    animation: 'fadeIn 0.2s ease-out',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.card,
    borderRadius: '24px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
    border: `2px solid ${theme.colors.primary}`,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
    animation: 'slideUp 0.3s ease-out',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: `1px solid ${theme.colors.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const infoBoxStyle: React.CSSProperties = {
    backgroundColor: 'rgba(0, 230, 118, 0.1)',
    border: `1px solid ${theme.colors.primary}`,
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '2rem',
    flexShrink: 0,
  };

  const infoTextStyle: React.CSSProperties = {
    flex: 1,
  };

  const infoTitleStyle: React.CSSProperties = {
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontSize: '1.1rem',
  };

  const infoDetailStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: theme.colors.textSecondary,
    marginBottom: '4px',
    fontSize: theme.fonts.sizeSmall,
  };

  const formGroupStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    color: theme.colors.text,
    fontWeight: theme.fonts.weightBold,
    fontSize: theme.fonts.sizeSmall,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '12px',
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit',
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  };

  const buttonStyle: React.CSSProperties = {
    flex: 1,
    padding: '16px',
    borderRadius: '12px',
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    color: '#181A1B',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: theme.colors.textSecondary,
    border: `1px solid ${theme.colors.border}`,
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    border: '1px solid rgba(255, 107, 107, 0.3)',
    borderRadius: '12px',
    padding: '12px 16px',
    color: '#ff6b6b',
    marginBottom: '16px',
    fontSize: theme.fonts.sizeSmall,
  };

  const loaderContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    gap: '16px',
  };

  if (loading) {
    return (
      <div style={overlayStyle} onClick={handleOverlayClick}>
        <div style={modalStyle}>
          <div style={loaderContainerStyle}>
            <Loader size={40} color={theme.colors.primary} className="spinner" />
            <p style={{ color: theme.colors.textSecondary }}>Cargando datos...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .spinner {
            animation: spin 1s linear infinite;
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(20px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={handleOverlayClick}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>Crear Partido desde Reserva</h2>
          <button
            style={closeButtonStyle}
            onClick={onClose}
            disabled={creating}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={24} color={theme.colors.textSecondary} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Info Box - Booking Details */}
          {prefillData && (
            <div style={infoBoxStyle}>
              <div style={iconStyle}>🏟️</div>
              <div style={infoTextStyle}>
                <div style={infoTitleStyle}>{prefillData.venue.name}</div>
                <div style={infoDetailStyle}>
                  <Calendar size={14} />
                  {new Date(prefillData.date).toLocaleDateString('es-AR')} a las {prefillData.time}
                </div>
                <div style={infoDetailStyle}>
                  <MapPin size={14} />
                  {prefillData.location}
                </div>
                <div style={infoDetailStyle}>
                  <Users size={14} />
                  Capacidad: {prefillData.maxPlayers} jugadores
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={errorStyle}>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Nombre del partido</label>
              <input
                type="text"
                style={inputStyle}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ej: Partidazo de amigos"
                required
                disabled={creating}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Descripción (opcional)</label>
              <textarea
                style={textareaStyle}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ej: Partido tranqui, llevá ganas y algo para tomar"
                disabled={creating}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ ...formGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Equipo 1</label>
                <input
                  type="text"
                  style={inputStyle}
                  value={formData.team1Name}
                  onChange={(e) => setFormData({ ...formData, team1Name: e.target.value })}
                  placeholder="Equipo 1"
                  disabled={creating}
                />
              </div>
              <div style={{ ...formGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Equipo 2</label>
                <input
                  type="text"
                  style={inputStyle}
                  value={formData.team2Name}
                  onChange={(e) => setFormData({ ...formData, team2Name: e.target.value })}
                  placeholder="Equipo 2"
                  disabled={creating}
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Máximo de jugadores por equipo</label>
              <input
                type="number"
                style={inputStyle}
                value={formData.maxPlayers}
                onChange={(e) => setFormData({ ...formData, maxPlayers: parseInt(e.target.value) })}
                min={2}
                max={30}
                required
                disabled={creating}
              />
              <small style={{ color: theme.colors.textSecondary, fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                El precio se divide automáticamente entre los jugadores
              </small>
            </div>

            {/* Button Group */}
            <div style={buttonGroupStyle}>
              <button
                type="button"
                style={secondaryButtonStyle}
                onClick={onClose}
                disabled={creating}
                onMouseEnter={(e) => {
                  if (!creating) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Cancelar
              </button>

              <button
                type="submit"
                style={primaryButtonStyle}
                disabled={creating}
                onMouseEnter={(e) => {
                  if (!creating) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(0, 230, 118, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {creating ? (
                  <>
                    <Loader size={18} className="spinner" />
                    Creando...
                  </>
                ) : (
                  <>
                    ⚽ Crear Partido
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};
